import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { AnalyticsOverview, AnalyticsSort } from "../models/AnalyticsOverview";

interface UseAnalyticsOverviewOptions {
  days: 7 | 30 | 90;
  connectionIds: string[];
  sort: AnalyticsSort;
  forceFreshOnMount?: boolean;
}

interface UseAnalyticsOverviewResult {
  data: AnalyticsOverview | null;
  loading: boolean;
  error: Error | null;
  preparing: boolean;
  preparationError: Error | null;
  prepareBackfill: () => Promise<void>;
  refetch: () => void;
}

function currentRange(days: number): { from: number; to: number } {
  const to = new Date();
  to.setHours(24, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - days);
  return { from: from.getTime(), to: to.getTime() };
}

export function useAnalyticsOverview({
  days,
  connectionIds,
  sort,
  forceFreshOnMount = false,
}: UseAnalyticsOverviewOptions): UseAnalyticsOverviewResult {
  const { currentUser, activeWorkspaceId } = useAuth();
  const [data, setData] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [preparationError, setPreparationError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const forceFreshRequestPending = useRef(forceFreshOnMount);
  const preparedRanges = useRef(new Set<string>());
  const backgroundRefresh = useRef(false);
  const connectionKey = useMemo(
    () => [...connectionIds].sort().join(","),
    [connectionIds],
  );

  useEffect(() => {
    if (!currentUser || !activeWorkspaceId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    // Progress polls refresh in place; only a user-driven load shows loading.
    const background = backgroundRefresh.current;
    backgroundRefresh.current = false;
    const load = async () => {
      if (!background) setLoading(true);
      setError(null);
      try {
        const token = await currentUser.getIdToken();
        const { from, to } = currentRange(days);
        const params = new URLSearchParams({
          from: String(from),
          to: String(to),
          sort,
        });
        const forceFreshRequest = forceFreshRequestPending.current;
        if (forceFreshRequest) params.set("refresh", "1");
        if (connectionKey) params.set("connectionIds", connectionKey);
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/analytics/overview?${params}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": activeWorkspaceId,
            },
            signal: controller.signal,
          },
        );
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.error || "Analytics couldn't be loaded.");
        }
        setData(body as AnalyticsOverview);
        if (forceFreshRequest) forceFreshRequestPending.current = false;
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setError(
          loadError instanceof Error
            ? loadError
            : new Error("Analytics couldn't be loaded."),
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void load();
    return () => controller.abort();
  }, [activeWorkspaceId, connectionKey, currentUser, days, refreshKey, sort]);

  const prepareBackfill = useCallback(async () => {
    if (!currentUser || !activeWorkspaceId || !data) return;
    setPreparing(true);
    setPreparationError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/analytics/prepare`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-workspace-id": activeWorkspaceId,
          },
          body: JSON.stringify({
            from: data.range.from,
            to: data.range.to,
            sort,
            connectionIds,
          }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || "Analytics preparation could not start.");
      }
      setData(body as AnalyticsOverview);
    } catch (prepareError) {
      setPreparationError(
        prepareError instanceof Error
          ? prepareError
          : new Error("Analytics preparation could not start."),
      );
    } finally {
      setPreparing(false);
    }
  }, [activeWorkspaceId, connectionIds, currentUser, data, sort]);

  // A daily refresh of an account that already imported is not setup work.
  const discoveryPending = data?.setup.discoveryConnections?.filter((connection) =>
    connection.completedAt === null &&
    ["queued", "running", "retrying"].includes(connection.status),
  ) ?? [];
  const discoveryKey = discoveryPending.map((connection) => connection.connectionId).sort().join(",");
  // Refreshes keep their numbers on screen, so they are queued once without
  // polling or a loading state.
  const refreshConnectionKey = [...(data?.setup.refreshConnectionIds ?? [])].sort().join(",");

  useEffect(() => {
    if (
      loading ||
      preparing ||
      (!data?.setup.backfillingConnections.length && !discoveryKey && !refreshConnectionKey)
    ) {
      return;
    }
    if (!data) return;
    const backfillingKey = data.setup.backfillingConnections
      .map((connection) => connection.connectionId)
      .sort()
      .join(",");
    const stalledConnections = data.setup.backfillingConnections.filter(
      (connection) => connection.collectionState === "stalled",
    );
    const stalledMarker = stalledConnections.length
      ? `:stalled:${Math.max(
          ...stalledConnections.map(
            (connection) => connection.lastActivityAt ?? 0,
          ),
        )}`
      : "";
    const key = `${activeWorkspaceId}:${backfillingKey}:${discoveryKey}:${refreshConnectionKey}${stalledMarker}`;
    if (preparedRanges.current.has(key)) return;
    preparedRanges.current.add(key);
    void prepareBackfill();
  }, [
    activeWorkspaceId,
    connectionKey,
    discoveryKey,
    data,
    loading,
    prepareBackfill,
    preparing,
    refreshConnectionKey,
  ]);

  // Poll quietly until updates land, so the "Updating…" indicator clears.
  const updating = (data?.setup.updatingConnectionIds?.length ?? 0) > 0;
  useEffect(() => {
    if (!data?.setup.backfillingConnections.length && !discoveryKey && !updating) return;
    const interval = window.setInterval(() => {
      backgroundRefresh.current = true;
      setRefreshKey((key) => key + 1);
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [data?.setup.backfillingConnections.length, discoveryKey, updating]);

  return {
    data,
    loading,
    error,
    preparing,
    preparationError,
    prepareBackfill,
    refetch: () => setRefreshKey((key) => key + 1),
  };
}
